import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { LinkedContentObjectsContract } from '@polaris-customerweb/sharedcomponents.core';
import { RoutingHelpersService } from '@polaris-customerweb/sharedcomponents.shared';
import { cloneDeep } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ShareCopyResponseDto } from 'src/app/dtos/share-copy-response.dto';
import { ShareDto, ShareItemDto } from 'src/app/dtos/share.dto';
import { Content } from 'src/app/models/content';
import { ContentCollection } from 'src/app/models/content-collection';
import { Share, ShareItem } from 'src/app/models/share';

@Injectable({ providedIn: 'root' })
export class ShareResourceService {
    private baseUrl: string;
    private publicSharesCache: Record<string, ShareDto | undefined> = {};
    private shareContentCache: Record<
        string,
        LinkedContentObjectsContract | undefined
    > = {};
    private collectionContentCache: Record<
        string,
        LinkedContentObjectsContract | undefined
    > = {};
    private rideEventContentCache: Record<
        string,
        LinkedContentObjectsContract | undefined
    > = {};
    private rideEventCollectionContentCache: Record<
        string,
        LinkedContentObjectsContract | undefined
    > = {};

    constructor(
        configService: ConfigService,
        private http: HttpClient,
        private routingHelpersService: RoutingHelpersService
    ) {
        this.baseUrl = routingHelpersService.combineUrl(
            configService.apiUrl.rideCommand,
            'shares'
        );
    }

    getSharesForUser(): Observable<Share[]> {
        const params = new HttpParams()
            .set('includeInactive', 'false')
            .set('includeDeleted', 'false');

        return this.http
            .get<ShareDto[]>(this.baseUrl, { params })
            .pipe(
                map((contracts) =>
                    contracts.map((contract) => new Share(contract))
                )
            );
    }

    getShareById(shareId: string): Observable<Share> {
        const cachedContract = this.publicSharesCache[shareId];
        if (cachedContract) {
            return of(new Share(cachedContract));
        }

        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            shareId
        );
        const params = new HttpParams().set('skipAuthorization', 'true');

        return this.http.get<ShareDto>(url, { params }).pipe(
            tap(
                (contract) =>
                    (this.publicSharesCache[shareId] = cloneDeep(contract))
            ),
            map((contract) => new Share(contract))
        );
    }

    getShareContents(shareId: string): Observable<Content> {
        const cachedContract = this.shareContentCache[shareId];
        if (cachedContract) {
            return of(Content.fromContentCollectionContract(cachedContract));
        }

        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            shareId,
            'contents'
        );
        const params = new HttpParams().set('skipAuthorization', 'true');

        return this.http
            .get<LinkedContentObjectsContract>(url, {
                params,
            })
            .pipe(
                tap(
                    (contract) =>
                        (this.shareContentCache[shareId] = cloneDeep(contract))
                ),
                // the shares API supports a share as a collection but we currently (and will probably always) only
                // allow a single item to be shared at atime.
                map((contract) =>
                    Content.fromContentCollectionContract(contract)
                )
            );
    }

    getSharedCollectionContents(
        shareId: string,
        collectionId: string
    ): Observable<ContentCollection> {
        const key = `${shareId}${collectionId}`;
        const cachedContract = this.collectionContentCache[key];
        if (cachedContract) {
            return of(new ContentCollection(cachedContract));
        }

        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            shareId,
            'collections',
            collectionId,
            'contents'
        );
        const params = new HttpParams().set('skipAuthorization', 'true');

        return this.http
            .get<LinkedContentObjectsContract>(url, { params })
            .pipe(
                tap(
                    (contract) =>
                        (this.collectionContentCache[key] = cloneDeep(contract))
                ),
                map((contract) => new ContentCollection(contract))
            );
    }

    getSharedRideEventContents(
        shareId: string,
        rideEventId: string
    ): Observable<ContentCollection> {
        const key = `${shareId}${rideEventId}`;
        const cachedContract = this.rideEventContentCache[key];
        if (cachedContract) {
            return of(new ContentCollection(cachedContract));
        }

        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            shareId,
            'events',
            rideEventId,
            'contents'
        );
        const params = new HttpParams().set('skipAuthorization', 'true');

        return this.http
            .get<LinkedContentObjectsContract>(url, { params })
            .pipe(
                tap(
                    (contract) =>
                        (this.rideEventContentCache[key] = cloneDeep(contract))
                ),
                map((contract) => new ContentCollection(contract))
            );
    }

    getSharedRideEventCollectionContents(
        shareId: string,
        rideEventId: string,
        collectionId: string
    ): Observable<ContentCollection> {
        const key = `${shareId}${rideEventId}${collectionId}`;
        const cachedContract = this.rideEventCollectionContentCache[key];

        if (cachedContract) {
            return of(new ContentCollection(cachedContract));
        }

        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            shareId,
            'collections',
            collectionId,
            'contents'
        );
        const params = new HttpParams()
            .set('eventId', rideEventId)
            .set('skipAuthorization', 'true');

        return this.http
            .get<LinkedContentObjectsContract>(url, { params })
            .pipe(
                tap(
                    (contract) =>
                        (this.rideEventCollectionContentCache[key] =
                            cloneDeep(contract))
                ),
                map((contract) => new ContentCollection(contract))
            );
    }

    update(share: Share): Observable<Share> {
        this.removeCaches(share.id);
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            share.id
        );

        return this.http.put(url, share.toDto()).pipe(map(() => share));
    }

    create(share: Share): Observable<Share> {
        return this.http
            .post<ShareDto>(this.baseUrl, share.toDto())
            .pipe(map((contract) => new Share(contract)));
    }

    reshare(share: Share): Observable<Share> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'reshare'
        );
        const items = share.sharedItems.map((i) => i.toDto());
        const reshare = {
            sharedItem: items[0],
        };

        return this.http
            .post<ShareDto>(url, reshare)
            .pipe(map((contract) => new Share(contract)));
    }

    delete(shareId: string): Observable<void> {
        this.removeCaches(shareId);
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            shareId
        );
        return this.http.delete<undefined>(url);
    }

    copyToAccount(
        shareId: string,
        shareItems: ShareItem[]
    ): Observable<ShareCopyResponseDto[]> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            shareId,
            'copy'
        );
        const shareItemsContracts = new Array<ShareItemDto>();
        for (const item of shareItems) {
            shareItemsContracts.push(item.toDto());
        }

        return this.http.post<ShareCopyResponseDto[]>(url, shareItemsContracts);
    }

    private removeCaches(shareId: string): void {
        this.shareContentCache[shareId] = undefined;
        const cacheIdRegex = new RegExp(`^${shareId}.*`);

        Object.keys(this.collectionContentCache).forEach((key) => {
            if (cacheIdRegex.test(key)) {
                this.collectionContentCache[key] = undefined;
            }
        });

        Object.keys(this.rideEventContentCache).forEach((key) => {
            if (cacheIdRegex.test(key)) {
                this.rideEventContentCache[key] = undefined;
            }
        });

        Object.keys(this.rideEventCollectionContentCache).forEach((key) => {
            if (cacheIdRegex.test(key)) {
                this.rideEventCollectionContentCache[key] = undefined;
            }
        });
    }
}
